<script setup>
import {
  ref, computed, watch, inject,
  nextTick,
} from 'vue';
import { incidencias as incidenciasApi, useCifraClient, utilsUsuarios } from 'cifra-api-js';
import InputCheckboxBoolean from '@/components/cifra/InputCheckboxBoolean.vue';
import BotonGuardar from '@/components/inputs/BotonGuardar.vue';
import { showApiErrors } from '@/libs/cifra/flash';
import toast from '@/libs/cifra/toast';
import Overlay from '@/libs/cifra/overlay';
import InputFechaHora from '@/components/cifra/InputFechaHora.vue';

const { cifraClient } = useCifraClient();
const I18n = inject('I18n');

const props = defineProps({
  alumno: {
    type: Object,
    required: true,
  },
  fecha: {
    type: String,
    required: true,
  },
  idGrupo: {
    type: Number,
    default: null,
  },
  idAsignatura: {
    type: Number,
    default: null,
  },
  permitirCambiarFecha: {
    type: Boolean,
    default: false,
  },
  // Si es true tras guardar la incidencia se recargará la página
  recargarPagina: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['cancelado', 'guardado']);

const incidencias = ref([]);
const idIncidencia = ref('');
const titulo = ref(null);
const mensaje = ref(null);
const notificar = ref(true);
const notificarTutoresAcademicos = ref(false);
const fechaInput = ref(props.fecha);

const incidencia = computed(() => (
  incidencias.value.find((inc) => inc.id === idIncidencia.value)
));

watch(incidencia, (newIncidencia) => {
  if (newIncidencia) {
    mensaje.value = newIncidencia.mensaje;
    titulo.value = newIncidencia.titulo;
  } else {
    mensaje.value = '';
    titulo.value = null;
  }
});

async function guardar() {
  try {
    const { data } = await cifraClient.post(`/alumnos/${props.alumno.id}/alumno_incidencias`, {
      alumno_incidencia: {
        alumno_id: props.alumno.id,
        incidencia_id: idIncidencia.value || null,
        grupo_id: props.idGrupo,
        asignatura_id: props.idAsignatura,
        fecha: props.permitirCambiarFecha ? fechaInput.value : props.fecha,
        titulo: titulo.value,
        comentario: mensaje.value,
        enviar_aviso: notificar.value,
        enviar_notificacion_tutores_academicos: notificarTutoresAcademicos.value,
      },
    });
    toast.success(I18n.t('Incidencia_creada_correctamente'));
    emit('guardado', data);
    if (props.recargarPagina) {
      nextTick(() => {
        new Overlay('body').show();
        window.location.reload();
      });
    }
  } catch (error) {
    showApiErrors(error);
  }
}

incidenciasApi.list().then((result) => {
  incidencias.value = result.data;
});

</script>

<template>
  <ThumbnailHeader
    :imagen="alumno.foto_url"
    :titulo="utilsUsuarios.fullname(alumno)"
    :subtitulo="$t('Incidencia')"
    tamano="md"
    class="cabecera border-bottom-gris py-3"
  />
  <form @submit.prevent="guardar">
    <InputFechaHora
      v-if="permitirCambiarFecha"
      v-model="fechaInput"
      :label="$t('Fecha')"
      class="mb-3"
    />
    <!-- Formulario de incidencia -->
    <div v-campo-requerido class="form-group">
      <label for="tipo-incidencia">Tipo de Incidencia</label>
      <select v-model="idIncidencia" id="tipo-incidencia" class="form-control">
        <option value="">
          -- {{ $t('manual') }} --
        </option>
        <option v-for="inc in incidencias" :key="inc.id" :value="inc.id">
          {{ inc.nombre }}
        </option>
      </select>
    </div>
    <div v-campo-requerido class="form-group">
      <label for="descripcion-incidencia">
        {{ $t('Titulo') }}
      </label>
      <input v-model="titulo" type="text" class="form-control" />
    </div>
    <div v-campo-requerido class="form-group">
      <label for="descripcion-incidencia">
        {{ $t('descripcion') }}
      </label>
      <textarea v-model="mensaje" class="form-control" rows="4"></textarea>
    </div>

    <InputCheckboxBoolean v-model="notificar">
      {{ $t('enviar_aviso') }}
    </InputCheckboxBoolean>
    <InputCheckboxBoolean v-model="notificarTutoresAcademicos" class="mt-3">
      {{ $t('enviar_notificacion_tutores_academicos') }}
    </InputCheckboxBoolean>
    <div class="form-actions mt-3">
      <button
        type="button"
        class="btn btn-default"
        :data-dismiss="recargarPagina ? 'modal' : null"
        @click="$emit('cancelado')"
      >
        {{ $t('Cancelar') }}
      </button>
      <BotonGuardar></BotonGuardar>
    </div>
  </form>
</template>

<style lang="scss" scoped>
@import '@/components/cuadernos/constantes_cuaderno.scss';

</style>
